/**
 * An org can hold one app row per tenant of the same vendor (two Microsoft
 * tenants give two "Microsoft" rows on one domain). Payloads for app rows carry
 * the tenant's display name and whether another live row shares the domain;
 * older payloads omit both, which must render exactly as a single-tenant app.
 */
export type AppTenantFields = {
  tenantName?: string | null;
  sharesDomain?: boolean;
};

type NamedAppRow = AppTenantFields & {
  name?: string | null;
  domain?: string | null;
};

const UNATTRIBUTED_TENANT_LABEL = "Unattributed";

/**
 * The tenant to name alongside a row whose domain another row shares: its own
 * tenant, or "Unattributed" for the domain's shadow row (sightings no
 * connected tenant claims). Null when the domain is not shared.
 */
export const tenantLabel = (app: AppTenantFields): string | null =>
  app.sharesDomain ? app.tenantName || UNATTRIBUTED_TENANT_LABEL : null;

/**
 * The name to show wherever a single app row is shown on its own:
 * "Microsoft (Contoso UK)" or "Microsoft (Unattributed)" when another row
 * shares the domain, otherwise the app's own name. Falls back to the domain
 * when the row has no name.
 */
export const appDisplayName = (app: NamedAppRow): string => {
  const base = app.name || app.domain || "";
  const tenant = tenantLabel(app);
  return tenant ? `${base} (${tenant})` : base;
};
